import React, { useState, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { insurancePartners } from '../mock';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const InsurancePartners = () => {
  const { language } = useLanguage();
  const [isPaused, setIsPaused] = useState(false);
  const scrollRef = useRef(null);

  const handlePrev = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const handleNext = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  return (
    <section className="py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {language === 'en' 
              ? 'Hablamos Español. Servimos a Todos.'
              : 'Hablamos Español. Servimos a Todos.'}
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            {language === 'en'
              ? 'At Palerm Insurance Agency, we are dedicated to providing customer-first service. More than just representatives, we are your dedicated guides, committed to helping you find the best insurance solutions tailored to your needs. Rooted in our Hispanic heritage and dedicated to serving a diverse clientele, we are here to protect what matters most to you.'
              : 'En Palerm Insurance Agency, nos dedicamos a brindar un servicio centrado en el cliente. Más que solo representantes, somos sus guías dedicados, comprometidos a ayudarlo a encontrar las mejores soluciones de seguro adaptadas a sus necesidades. Arraigados en nuestra herencia hispana y dedicados a servir a una clientela diversa, estamos aquí para proteger lo que más le importa.'}
          </p>
          <p className="text-sm text-gray-500 mt-4 italic">
            {language === 'en'
              ? 'Access to carriers through various partnerships including First Connect'
              : 'Acceso a aseguradoras a través de varias asociaciones incluyendo First Connect'}
          </p>
        </div>

        {/* Partner Logos Carousel */}
        <div className="relative">
          {/* Previous Button */}
          <button
            onClick={handlePrev}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-3 transition-all hidden md:block"
            aria-label="Previous carriers"
          >
            <ChevronLeft className="w-6 h-6 text-gray-700" />
          </button>

          {/* Next Button */}
          <button
            onClick={handleNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-3 transition-all hidden md:block"
            aria-label="Next carriers"
          >
            <ChevronRight className="w-6 h-6 text-gray-700" />
          </button>

          {/* Scrollable Container */}
          <div
            ref={scrollRef}
            className="overflow-x-auto overflow-y-hidden scrollbar-hide px-12"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            onFocus={() => setIsPaused(true)}
            onBlur={() => setIsPaused(false)}
            style={{
              scrollBehavior: 'smooth',
              WebkitOverflowScrolling: 'touch'
            }}
          >
            <div 
              className={`flex gap-6 items-center py-8 ${!isPaused ? 'animate-scroll-partners' : ''}`}
              style={{
                animationPlayState: isPaused ? 'paused' : 'running'
              }}
            >
              {[...insurancePartners, ...insurancePartners].map((partner, index) => (
                <div
                  key={`partner-${index}`}
                  className="flex-shrink-0 w-36 h-28 bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 p-4 flex flex-col items-center justify-center gap-2"
                  role="img"
                  aria-label={partner.name}
                >
                  <img
                    src={partner.logo}
                    alt=""
                    className="max-w-full max-h-16 object-contain"
                    loading="lazy"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.style.display = 'none';
                      const span = e.target.nextElementSibling;
                      if (span) span.style.display = 'block';
                    }}
                  />
                  <span className="text-xs font-semibold text-gray-700 text-center leading-tight hidden">
                    {partner.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Total Count */}
        <div className="text-center mt-8">
          <p className="text-sm text-gray-600">
            {language === 'en' ? 'Serving you with' : 'Sirviéndole con'}{' '}
            <strong className="text-blue-700">{insurancePartners.length}+</strong>{' '}
            {language === 'en' ? 'insurance markets' : 'mercados de seguros'}
          </p>
        </div>
      </div>

      <style jsx global>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        @media (prefers-reduced-motion: reduce) {
          .animate-scroll-partners {
            animation: none !important;
          }
        }
      `}</style>
    </section>
  );
};

export default InsurancePartners;
